import { usePageTitle } from '@/hooks/usePageTitle';
import { useEffect, useMemo, useState, useRef, useCallback, type CSSProperties, type ReactNode } from 'react';
import { Download, CheckCircle, XCircle, Loader2, PenTool, TriangleAlert, Plus, Minus, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { A4_WIDTH_PX } from '@/constants/a4';
import { isRenderableBlock } from '@/lib/blockHelpers';
import { usePublicQuote } from '@/hooks/usePublicQuote';
import { QuoteClientLogoProvider } from '@/store/quoteClientLogoContext';
import RespondDialog from '@/components/public-quote/RespondDialog';
import BoxSignDialog from '@/components/public-quote/BoxSignDialog';
import VideoIntroModal from '@/components/public-quote/VideoIntroModal';
import { getPublicQuoteStrings, normalizePublicQuoteLocale } from '@/lib/publicQuoteLocale';
import QuoteDocument, { createQuoteRenderInput } from '@/components/quote-document/QuoteDocument';
import TiptapQuoteDocument from '@/components/quote-document/TiptapQuoteDocument';
import {
  QuotePublicViewProvider,
  type QuotePublicView,
} from '@/components/quote-document/QuotePublicView';

const PAGE_W_PX = A4_WIDTH_PX;
/** Achtergrond van de pagina; ook de tussenruimte tussen de vellen. */
const PAGE_BACKGROUND = '#f5f6f8';
/** Horizontale marge voor fit-berekening op desktop (TiptapQuoteEditor). Op smalle viewports kleiner voor grotere zoom. */
const PAGE_HORIZONTAL_PADDING_DESKTOP = 64;
const PAGE_HORIZONTAL_PADDING_MOBILE = 16;
/** Zoom-knoppen & pinch: extra user-zoom bovenop de fit-scale. */
const USER_ZOOM_MIN = 0.5;
const USER_ZOOM_MAX = 3;
const USER_ZOOM_STEP = 0.2;
/** Paginateller: een kaart telt als huidig zodra zijn bovenkant deze fractie van het scherm passeert. */
const READING_LINE_RATIO = 0.4;

function clampUserZoom(v: number) {
  return Math.min(USER_ZOOM_MAX, Math.max(USER_ZOOM_MIN, v));
}
/**
 * Cross-browser schaal i.p.v. CSS `zoom` (Safari/Firefox inconsistent).
 * Buitenste box reserveert de geschaalde ruimte; transform schaalt alleen
 * visueel, dus de layout (en daarmee de paginering) is gelijk aan de PDF.
 */
function ScaledQuoteDocument({ scale, children }: { scale: number; children: ReactNode }) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="shrink-0" style={{ width: PAGE_W_PX * scale, height: height * scale, position: 'relative' }}>
      <div
        ref={innerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: PAGE_W_PX,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PublicQuotePage() {
  const {
    slug,
    quote,
    loading,
    error,
    isPrintMode,
    passwordRequired,
    password,
    setPassword,
    passwordError,
    handlePasswordSubmit,
    signStatus,
    signEmbedUrl,
    signatureData,
    respondDialog,
    setRespondDialog,
    signerName,
    setSignerName,
    message,
    setMessage,
    responding,
    isAccepted,
    isResponded,
    hasActiveSignRequest,
    hasSignaturePage,
    savedSignature,
    handleSignatureChange,
    handleDownloadPDF,
    pdfDownloading,
    scrollToSignatureArea,
    handleAcceptAfterSign,
    handleSignAsRecipient,
    signingRecipientId,
    pendingRecipientCount,
    awaitingOtherSigners,
    handleRespond,
    isDraftPublic,
  } = usePublicQuote();

  usePageTitle('Offerte', quote?.title);

  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const [layoutHorizontalPad, setLayoutHorizontalPad] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
      ? PAGE_HORIZONTAL_PADDING_MOBILE
      : PAGE_HORIZONTAL_PADDING_DESKTOP
  );
  const [signIframeOpen, setSignIframeOpen] = useState(false);

  const effectiveScale = scale * userZoom;
  const zoomIn = useCallback(() => setUserZoom((v) => clampUserZoom(v + USER_ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setUserZoom((v) => clampUserZoom(v - USER_ZOOM_STEP)), []);
  const zoomReset = useCallback(() => setUserZoom(1), []);
  /** De paginacontainer bestaat pas na het laden; effecten erop moeten dan opnieuw lopen. */
  const pagesMounted = !loading && Boolean(quote);

  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el || isPrintMode) return;
    const compute = () => {
      const pad = el.clientWidth < 640 ? PAGE_HORIZONTAL_PADDING_MOBILE : PAGE_HORIZONTAL_PADDING_DESKTOP;
      setLayoutHorizontalPad(pad);
      const available = el.clientWidth - pad;
      setIsMobileViewport(el.clientWidth < 640);
      // Ook op een telefoon past het hele vel, net als in een PDF-viewer; de klant zoomt zelf in.
      setScale(Math.min(1, available / PAGE_W_PX));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isPrintMode, pagesMounted]);

  /** Eén anker per A4-vel, gelijk aan de pagina's van de PDF (zie TiptapQuoteDocument). */
  const collectPublicPageElements = useCallback((): HTMLElement[] => {
    const container = pagesContainerRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>('[data-sheet-index]'));
  }, []);

  /**
   * Huidige pagina = het laatste vel waarvan de bovenkant de leeslijn is gepasseerd.
   * Werkt ook als een vel (ingezoomd op mobiel) hoger is dan het scherm.
   */
  useEffect(() => {
    const container = pagesContainerRef.current;
    if (!pagesMounted || !container) return;
    let pageEls: HTMLElement[] = [];
    let frame = 0;

    const updateCurrentPage = () => {
      frame = 0;
      const scrolledToEnd =
        window.scrollY > 0 &&
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (scrolledToEnd) {
        setCurrentPage(Math.max(0, pageEls.length - 1));
        return;
      }
      const readingLine = window.innerHeight * READING_LINE_RATIO;
      let current = 0;
      pageEls.forEach((element, index) => {
        if (element.getBoundingClientRect().top <= readingLine) current = index;
      });
      setCurrentPage(current);
    };
    const scheduleUpdate = () => {
      if (!frame) frame = requestAnimationFrame(updateCurrentPage);
    };
    const collectPages = () => {
      pageEls = collectPublicPageElements();
      setTotalPages(pageEls.length);
      scheduleUpdate();
    };
    collectPages();

    const mutationObserver = new MutationObserver(collectPages);
    mutationObserver.observe(container, { childList: true, subtree: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      mutationObserver.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      cancelAnimationFrame(frame);
    };
  }, [collectPublicPageElements, pagesMounted]);

  /**
   * Pinch-to-zoom op de kaart-container.
   * Twee vingers = user-zoom; één vinger blijft horizontaal/verticaal scrollen.
   */
  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el || isPrintMode) return;
    let startDist = 0;
    let startZoom = 1;
    const distance = (a: Touch, b: Touch) => {
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      startDist = distance(e.touches[0], e.touches[1]);
      startZoom = userZoom;
    };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || startDist === 0) return;
      e.preventDefault();
      const d = distance(e.touches[0], e.touches[1]);
      const next = clampUserZoom(startZoom * (d / startDist));
      setUserZoom(next);
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) startDist = 0;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [isPrintMode, pagesMounted, userZoom]);

  const nlPage = getPublicQuoteStrings('nl').page;
  const nlHook = getPublicQuoteStrings('nl').hook;
  /**
   * Stabiele referentie: een nieuwe array laat TiptapQuoteDocument het hele document
   * vervangen. Dat wist de paginering, en de scroll anchoring van de browser zet de
   * lezer dan terug zodra de paginateller tijdens het scrollen verspringt.
   */
  const renderableBlocks = useMemo(() => quote?.blocks.filter(isRenderableBlock) ?? [], [quote?.blocks]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">{nlPage.loading}</p>
      </div>
    );
  }

  if (passwordRequired && !quote) {
    return (
      <div className="flex justify-center items-center min-h-screen px-4">
        <div className="w-full p-4 sm:p-8 text-center max-w-md border rounded-lg bg-card shadow-sm">
          <h2 className="text-xl font-semibold mb-2">{nlPage.passwordTitle}</h2>
          <p className="text-muted-foreground mb-6">{nlPage.passwordDescription}</p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-2 mb-4">
              <Label htmlFor="password">{nlPage.passwordLabel}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={passwordError ? 'border-destructive' : ''}
              />
              {passwordError && (
                <p className="text-sm text-destructive">{nlPage.passwordInvalid}</p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading || !password}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : nlPage.passwordSubmit}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    const isExpiredError = error === nlHook.expired || error === getPublicQuoteStrings('en').hook.expired;
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#f5f6f8] px-4">
        <div className="w-full p-4 sm:p-8 text-center max-w-md border rounded-lg bg-card shadow-sm">
          {isExpiredError ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                <XCircle className="h-6 w-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">{nlPage.expiredTitle}</h2>
              <p className="text-muted-foreground">{nlPage.expiredBody}</p>
              <p className="text-muted-foreground mt-2 text-sm">{nlPage.expiredContact}</p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold mb-2">{error || nlPage.errorNotFoundTitle}</h2>
              <p className="text-muted-foreground">{nlPage.errorNotFoundBody}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const viewerLocale = normalizePublicQuoteLocale(quote.publicLocale);
  const quoteRenderInput = createQuoteRenderInput(quote);
  const t = getPublicQuoteStrings(viewerLocale);

  const displayTotalPages = totalPages || renderableBlocks.length;
  const progress = displayTotalPages > 0 ? ((currentPage + 1) / displayTotalPages) * 100 : 0;

  const existingSignature = savedSignature || (isAccepted ? signatureData : null);
  // De PDF toont dezelfde pagina's als de klantlink, alleen zonder bediening.
  const printView: QuotePublicView = {
    quoteStatus: quote.status,
    publicLocale: viewerLocale,
    boxSignStatus: signStatus,
    existingSignature,
  };
  const webView: QuotePublicView = {
    ...printView,
    onSignature: isDraftPublic ? undefined : handleSignatureChange,
    onAccept: isResponded || isDraftPublic ? undefined : handleAcceptAfterSign,
    acceptLoading: responding,
    onSignAsRecipient: isResponded || isDraftPublic ? undefined : handleSignAsRecipient,
    signingRecipientId,
  };

  if (isPrintMode) {
    return (
      <QuoteClientLogoProvider blocks={quote.blocks} layoutConfig={quote.layoutConfig}>
        <QuotePublicViewProvider value={printView}>
          <QuoteDocument mode="print" input={quoteRenderInput} className="contents">
            <style>{`
              @page { size: A4 landscape; margin: 0; }
              html, body { margin: 0; padding: 0; background: #ffffff; }
            `}</style>
            <TiptapQuoteDocument
              blocks={renderableBlocks}
              variablesMap={quote.variableMap}
              renderHash={quoteRenderInput.hash}
            />
          </QuoteDocument>
        </QuotePublicViewProvider>
      </QuoteClientLogoProvider>
    );
  }

  return (
    <QuoteClientLogoProvider blocks={quote.blocks} layoutConfig={quote.layoutConfig}>
    <QuoteDocument mode="web" input={quoteRenderInput} className="contents">
    <div
      className={`min-h-screen ${!isResponded && !isDraftPublic ? 'pb-36 sm:pb-28' : ''}`}
      style={{ background: PAGE_BACKGROUND, '--quote-sheet-gap-color': PAGE_BACKGROUND } as CSSProperties}
    >
      {pdfDownloading && (
        <div
          className="print:hidden fixed inset-0 z-[150] flex flex-col items-center justify-center gap-4 bg-background/75 backdrop-blur-[1px]"
          role="status"
          aria-live="polite"
          aria-label={t.page.pdfGenerating}
        >
          <Loader2 className="h-11 w-11 animate-spin text-primary" aria-hidden />
          <p className="max-w-sm px-6 text-center text-sm font-medium text-foreground">{t.page.pdfGenerating}</p>
        </div>
      )}

      {slug && quote.introVideoUrl ? (
        <VideoIntroModal
          slug={slug}
          videoUrl={quote.introVideoUrl}
          quoteTitle={quote.title}
          isPrintMode={isPrintMode}
          locale={viewerLocale}
        />
      ) : null}

      {/* Sticky top bar */}
      <header className="sticky top-0 z-[100] bg-white border-b shadow-sm rounded-none print:hidden">
        <div className="max-w-[1100px] mx-auto flex flex-col gap-2 px-4 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 sm:px-6 sm:py-3">
          <div className="min-w-0 sm:flex-1">
            <p className="font-semibold truncate">{quote.title}</p>
            <p className="text-xs font-medium text-foreground sm:text-sm">
              <span className="sm:hidden">
                {currentPage + 1}/{displayTotalPages}
              </span>
              <span className="hidden sm:inline">
                {t.page.pageLabel} {currentPage + 1} {t.page.pageOfWord} {displayTotalPages}
              </span>
            </p>
          </div>

          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:flex-none">
            {isDraftPublic && (
              <span className="inline-flex shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">
                Concept
              </span>
            )}

            {signStatus === 'signed' && (
              <Badge variant="default" className="flex shrink-0 items-center gap-1 bg-green-600 text-xs sm:text-sm">
                <PenTool className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="max-w-[9rem] truncate sm:max-w-none">{t.page.signedBadge}</span>
              </Badge>
            )}

            {isResponded && !signStatus && (
              <Badge
                variant={isAccepted ? 'default' : 'destructive'}
                className="flex shrink-0 items-center gap-1 text-xs sm:text-sm"
              >
                {isAccepted ? <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <XCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                <span className="max-w-[9rem] truncate sm:max-w-none">
                  {isAccepted ? t.page.acceptedBadge : t.page.declinedBadge}
                </span>
              </Badge>
            )}

            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPDF}
              className="gap-1 sm:min-w-[7.5rem]"
              disabled={isDraftPublic || pdfDownloading}
              title={isDraftPublic ? t.page.draftPdfDisabled : undefined}
              aria-busy={pdfDownloading}
              aria-label={pdfDownloading ? t.page.pdfGenerating : t.page.pdf}
            >
              {pdfDownloading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4 shrink-0" aria-hidden />
              )}
              <span className="hidden sm:inline">{pdfDownloading ? t.page.pdfGenerating : t.page.pdf}</span>
            </Button>

            {hasActiveSignRequest && !isResponded && !isDraftPublic && (
              <Button
                size="sm"
                className="gap-1 bg-blue-600 px-3 hover:bg-blue-700 sm:px-6"
                onClick={() => setSignIframeOpen(true)}
              >
                <PenTool className="h-4 w-4 shrink-0" />
                <span className="max-w-[10rem] truncate sm:max-w-none">{t.page.signThisQuote}</span>
              </Button>
            )}

            {!isResponded && !hasActiveSignRequest && !isDraftPublic && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-destructive px-2 text-destructive hover:bg-destructive/10 sm:px-3"
                  onClick={() => setRespondDialog('declined')}
                >
                  <XCircle className="h-4 w-4 shrink-0" />
                  <span className="max-w-[5.5rem] truncate text-xs sm:max-w-none sm:text-sm">{t.page.decline}</span>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="gap-1 bg-green-600 px-3 hover:bg-green-700 sm:px-6"
                  onClick={hasSignaturePage ? scrollToSignatureArea : () => setRespondDialog('accepted')}
                >
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  <span className="max-w-[8rem] truncate sm:max-w-none">{t.page.accept}</span>
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="h-0.5 bg-primary/20">
          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </header>

      {isDraftPublic && (
        <div className="print:hidden bg-[#f5f6f8]">
          <div className="mx-auto max-w-[1100px] px-4 pt-3 sm:px-6 sm:pt-4">
            <Alert variant="warning" className="border-amber-300">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle className="text-amber-950">Concept</AlertTitle>
              <AlertDescription className="text-amber-950/90">{t.page.draftBanner}</AlertDescription>
            </Alert>
          </div>
        </div>
      )}

      {/* Pages: hetzelfde document en dezelfde vellen als de PDF */}
      <div
        ref={pagesContainerRef}
        className="mx-auto overflow-x-auto px-2 py-4 sm:overflow-x-visible sm:px-4 sm:py-8"
        style={{ maxWidth: PAGE_W_PX + layoutHorizontalPad }}
      >
        <div className="flex w-full justify-start sm:justify-center">
          <ScaledQuoteDocument scale={effectiveScale}>
            <QuotePublicViewProvider value={webView}>
              <TiptapQuoteDocument
                blocks={renderableBlocks}
                variablesMap={quote.variableMap}
                renderHash={quoteRenderInput.hash}
              />
            </QuotePublicViewProvider>
          </ScaledQuoteDocument>
        </div>
      </div>

      {/* Floating zoom-controls (mobiel): pinch of knoppen */}
      {isMobileViewport && (
        <div
          className="fixed right-3 z-[110] flex flex-col items-stretch gap-0 overflow-hidden rounded-full border bg-white/95 shadow-lg backdrop-blur print:hidden"
          style={{
            bottom: `calc(${!isResponded && !isDraftPublic ? '8.5rem' : '1rem'} + env(safe-area-inset-bottom, 0px))`,
          }}
          role="group"
          aria-label="Zoom"
        >
          <button
            type="button"
            onClick={zoomIn}
            disabled={userZoom >= USER_ZOOM_MAX - 0.001}
            className="flex h-10 w-10 items-center justify-center text-foreground transition hover:bg-muted disabled:opacity-40"
            aria-label="Inzoomen"
          >
            <Plus className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={zoomReset}
            className="flex h-9 w-10 items-center justify-center border-t border-b text-[10px] font-semibold text-muted-foreground transition hover:bg-muted"
            aria-label="Zoom terugzetten"
            title={`${Math.round(userZoom * 100)}%`}
          >
            {userZoom === 1 ? <Maximize2 className="h-4 w-4" aria-hidden /> : `${Math.round(userZoom * 100)}%`}
          </button>
          <button
            type="button"
            onClick={zoomOut}
            disabled={userZoom <= USER_ZOOM_MIN + 0.001}
            className="flex h-10 w-10 items-center justify-center text-foreground transition hover:bg-muted disabled:opacity-40"
            aria-label="Uitzoomen"
          >
            <Minus className="h-5 w-5" aria-hidden />
          </button>
        </div>
      )}

      {/* Bottom CTA bar (fixed) */}
      {!isResponded && !isDraftPublic && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[100] border-t bg-white shadow-lg print:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="mx-auto flex max-w-[1100px] flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4">
            <p className="hidden flex-1 text-sm text-muted-foreground sm:block">
              {hasActiveSignRequest
                ? t.page.ctaAwaitingSign
                : awaitingOtherSigners
                  ? t.page.ctaAwaitingOtherSigners.replace('{remaining}', String(pendingRecipientCount))
                  : t.page.ctaReady}
            </p>
            {hasActiveSignRequest ? (
              <Button
                size="lg"
                className="w-full gap-2 bg-blue-600 hover:bg-blue-700 sm:w-auto sm:px-8"
                onClick={() => setSignIframeOpen(true)}
              >
                <PenTool className="h-5 w-5 shrink-0" />
                {t.page.signThisQuote}
              </Button>
            ) : (
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end sm:gap-3">
                <Button
                  variant="outline"
                  className="w-full border-destructive text-destructive sm:w-auto"
                  onClick={() => setRespondDialog('declined')}
                >
                  {t.page.decline}
                </Button>
                <Button
                  size="lg"
                  className="w-full bg-green-600 hover:bg-green-700 sm:w-auto sm:px-8"
                  onClick={hasSignaturePage ? scrollToSignatureArea : () => setRespondDialog('accepted')}
                >
                  {t.page.acceptQuote}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <RespondDialog
        open={respondDialog}
        onOpenChange={setRespondDialog}
        signerName={signerName}
        onSignerNameChange={setSignerName}
        message={message}
        onMessageChange={setMessage}
        responding={responding}
        onRespond={handleRespond}
        locale={viewerLocale}
      />

      <BoxSignDialog
        open={signIframeOpen}
        onOpenChange={setSignIframeOpen}
        embedUrl={signEmbedUrl}
        locale={viewerLocale}
      />
    </div>
    </QuoteDocument>
    </QuoteClientLogoProvider>
  );
}

export default PublicQuotePage;
